import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import MaintenanceLogReportEditDialog from './MaintenanceLogReportEditDialog/MaintenanceLogReportEditDialog';

/**
 * 
 * @param {*} props 
 * Props:
 * - vehicle
 * - startDate
 * - endDate
 * - refresh
 * @returns MaintenanceLogReportDataTable
 */
function MaintenanceLogReportDataTable(props) {

  // #region Vars and Consts

  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceTypeConcepts, setServiceTypeConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogIsVisible, setEditDialogIsVisible] = useState(false);
  const [selectedMaintenanceLog, setSelectedMaintenanceLog] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [filters, setFilters] = useState({
    'global': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },    
    "vehicleServiceId": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },     
    "vehicle.vehicleId": {
      operator: FilterOperator.OR,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.EQUALS
      }]
    },    
    "createdAt": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "serviceType.serviceTypeId": {
      operator: FilterOperator.OR,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.EQUALS
      }]
    },
    "serviceTypeConcept.serviceTypeConceptId": {
      operator: FilterOperator.OR,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.EQUALS
      }]
    },
    "amountWTax": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "notes": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "supplier": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "dischargeDate": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "user": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    }
  });

  // #endregion
  
  // #region Events

  useEffect(() => {
    getVehicles();
    getServiceTypes();
    getServiceTypeConcepts();
  }, []);
  
  useEffect(() => {    
    getMaintenanceLogs(props.vehicle.vehicleId, props.startDate.valueOf(), props.endDate.valueOf());
  }, [props.refresh]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilter(value);
  };

  const onNewClick = () => {    
    setSelectedMaintenanceLog(null);
    setEditDialogIsVisible(true);
  };

  const onEditClick = (maintenanceLog) => {
    setSelectedMaintenanceLog(maintenanceLog);
    setEditDialogIsVisible(true);
  };

  const onRemoveClick = (maintenanceLog) => {
    setSelectedMaintenanceLog(maintenanceLog);
    confirmRemove();
  };

  const onMaintenanceLogSaved = () => {
    setEditDialogIsVisible(false);
    getMaintenanceLogs(props.vehicle.vehicleId, props.startDate.valueOf(), props.endDate.valueOf());
  };

  const onAcceptRemove = () => {
    remove(selectedMaintenanceLog.vehicleServiceId);      
  };

  const onRejectRemove = () => {  
    setSelectedMaintenanceLog(null);
  };

  // #endregion

  // #region Methods

  const getMaintenanceLogs = (vehicleId, startDate, endDate) => {    
    fetch(`${process.env.REACT_APP_API_URL}/vehicleService/findAll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vehicleId,
        startDate,
        endDate
      }),
    }).then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {          
          setMaintenanceLogs(data.responseObject);
        } else {
          console.warn('Error getting maintenance log', data);
        }        
      }, (error) => {
        console.error("Error getting maintenance log", error);        
      });
  };
  
  const getVehicles = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/vehicle/cmb?generic=true`)
      .then(response => response.json())
      .then(data => {        
        if (data.responseCode === 'OK') {
          setVehicles(data.responseObject);
        } else {
          console.warn('Error getting vehicles', data);
        }        
      }, (error) => {
        console.error("Error getting vehicles", error);        
      });
  };  

  const getServiceTypes = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/serviceType/cmb?generic=true`)
      .then(response => response.json())
      .then(data => {        
        if (data.responseCode === 'OK') {
          setServiceTypes(data.responseObject);
        } else {
          console.warn('Error getting service types', data);
        }        
      }, (error) => {
        console.error("Error getting service types", error);        
      });
  };  

  const getServiceTypeConcepts = () => {
    fetch(`${process.env.REACT_APP_API_URL}/serviceTypeConcept/cmb?generic=true`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setServiceTypeConcepts(data.responseObject);
        } else {
          console.warn('Error getting service type concepts', data);
        }
      }, (error) => {
        console.error("Error getting service type concepts", error);
      });
  };  

  const confirmRemove = () => {
    confirmDialog({
      message: '¿Quieres eliminar este registro?',
      header: 'Eliminar ',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: onAcceptRemove,
      reject: onRejectRemove,
      acceptLabel:'Sí',
      rejectLabel:'No'
    });    
  };

  const remove = (
    vehicleServiceId
  ) => {
    console.group("Remove vehicle service");
    fetch(`${process.env.REACT_APP_API_URL}/vehicleService/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vehicleServiceId
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("Maintenance log removed");
        getMaintenanceLogs(props.vehicle.vehicleId, props.startDate.valueOf(), props.endDate.valueOf());
      } else {
        console.warn("Error removing maintenance log");
      }
      console.groupEnd()
    }, (error) => {
      console.error("Error removing maintenance log", error);
      console.groupEnd();
    });
  };
  
  // #endregion

  // #region Templates

  const tableHeaderTemplate = (
    <>
      <Row>
        <Col md={8}>
          <span className="p-input-icon-left">
            <i className="pi pi-filter" />
            <InputText
              placeholder="Filtrar"
              value={globalFilter}
              onChange={onGlobalFilterChange} />
          </span>
        </Col>
        <Col md={4}>
          <Button
            label="Nuevo mantenimiento"
            className="p-button-raised p-button-sm float-end"
            onClick={onNewClick}
          />
        </Col>
      </Row>
    </>
  );

  const actionsColumnTemplate = (maintenanceLog) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-primary p-button-text mr-2"
          onClick={() => onEditClick(maintenanceLog)}             
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text mr-2"
          onClick={() => onRemoveClick(maintenanceLog)}         
        />
      </>
    );
  };  

  const vehicleColumnTemplate = (maintenance) => {
    return maintenance.vehicle.description;
  };

  const serviceTypeColumnTemplate = (maintenance) => {
    return maintenance.serviceType.description;
  };

  const serviceTypeConceptColumnTemplate = (maintenance) => {
    return maintenance.serviceTypeConcept.description;
  };

  const vehicleColumnFilterItemTemplate = (option) => {
    return <span>{option.label}</span>;
  };  

  const serviceTypeColumnFilterItemTemplate = (option) => {
    return <span>{option.label}</span>;
  };

  const serviceTypeConceptColumnFilterItemTemplate = (option) => {
    return <span>{option.label}</span>;
  };

  const vehicleColumnFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={vehicles}
        onChange={(e) => options.filterCallback(e.value)}
        itemTemplate={vehicleColumnFilterItemTemplate}
        placeholder="Selecciona un vehículo"
        showClear       
      />
    );
  };

  const serviceTypeColumnFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={serviceTypes}
        onChange={(e) => options.filterCallback(e.value)}
        itemTemplate={serviceTypeColumnFilterItemTemplate}
        placeholder="Selecciona un tipo de servicio"
        showClear />
    );
  };

  const serviceTypeConceptColumnFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={serviceTypeConcepts}
        onChange={(e) => options.filterCallback(e.value)}
        itemTemplate={serviceTypeConceptColumnFilterItemTemplate}
        placeholder="Selecciona un concepto de tipo de servicio"
        showClear />
    );
  };

  const amountWTaxColumnTemplate = (rowData) => {
    return rowData.amountWTax.toFixed(2);
  };

  const tableTemplate = (
    <>
      <div className="card">
        <DataTable
          className=""
          showGridlines
          stripedRows
          size="small"
          responsiveLayout="scroll"
          emptyMessage="No se encontró bitácora"
          value={maintenanceLogs}
          header={tableHeaderTemplate}
          filters={filters}
          filterDisplay="menu"
          loading={loading}
          resizableColumns
          columnResizeMode="fit"
          globalFilterFields={[            
            "vehicleServiceId",
            "vehicle.description",
            "createdAt",
            "serviceType.description",
            "serviceTypeConcept.description",
            "amountWTax",
            "notes",
            "supplier",
            "dischargeDate",
            "user"            
          ]}>
          <Column
            className='app-w-50-px text-center'
            body={actionsColumnTemplate}
            exportable={false}               
          />
          <Column
            field="vehicleServiceId"
            header="Folio"
            className='app-w-200-px text-end'
            filter
            filterPlaceholder="Buscar por Folio"
            sortable
          />
          <Column
            field="vehicle.vehicleId"
            header="Vehículo"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Tipo de vehículo"
            filterElement={vehicleColumnFilterTemplate}
            body={vehicleColumnTemplate}
            sortable
          />
          <Column
            field="createdAt"
            header="Fecha registro"
            className='app-w-200-px text-end'
            filter
            filterPlaceholder="Buscar por Fecha registro "
            sortable
          />
          <Column
            field="serviceType.serviceTypeId"
            header="Tipo de servicio"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Tipo de servicio"
            filterElement={serviceTypeColumnFilterTemplate}
            body={serviceTypeColumnTemplate}
            sortable           
          />
          <Column
            field="serviceTypeConcept.serviceTypeConceptId"
            header="Concepto de tipo de servicio"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Concepto de tipo de servicio"
            filterElement={serviceTypeConceptColumnFilterTemplate}
            body={serviceTypeConceptColumnTemplate}
            sortable
          />    
          <Column
            field="amountWTax"
            body={amountWTaxColumnTemplate}
            header="Costo con IVA"
            className='app-w-200-px text-end'
            filter
            filterPlaceholder="Buscar por Monto"
            sortable
          />
          <Column
            field="notes"
            header="Observaciones"
            className='app-w-200-px'
            filter
            filterPlaceholder="Buscar por Observaciones"
            sortable
          />
          <Column
            field="supplier"
            header="Taller / Técnico / Proveedor"
            className='app-w-200-px'
            filter
            filterPlaceholder="Buscar por Taller / Técnico / Proveedor"
            sortable
          />
          <Column
            field="dischargeDate"
            header="Fecha de alta"
            className='app-w-200-px'
            filter
            filterPlaceholder="Buscar por Fecha de alta"
            sortable
          />
          <Column
            field="user"
            header="Usuario"
            className='app-w-200-px'
            filter
            filterPlaceholder="Buscar por Usuario"
            sortable
          />
        </DataTable>
      </div>
    </>
  );

  const removeConfirmationDialog = (
    <>
      <ConfirmDialog />      
    </>
  )
  
  const template = (
    <>
      {tableTemplate}
      <MaintenanceLogReportEditDialog
        visible={editDialogIsVisible}
        maintenanceLog={selectedMaintenanceLog}
        onHide={() => setEditDialogIsVisible(false)}
        onMaintenanceLogSaved={onMaintenanceLogSaved}
      />
      {removeConfirmationDialog}
    </>
  );

  // #endregion

  return (
    <>
      {template}
    </>
  );
}

export default MaintenanceLogReportDataTable;